import { isDefined } from '@morpho-org/morpho-ts'

import type { Warning } from '@/components/common/WarningIcon/WarningHoverCard/WarningHoverCardContent'

import {
  getVaultV2WarningMessage,
  getVaultWarningMessage,
  getVaultWarningScrollType
} from '@/helpers/warnings'

import type { UserEarnTransaction } from '../earnTransactions.contract'

import { toWarningLevel } from './activityWarningLevel'

// Lives outside the hook so it can be unit-tested: importing the hook pulls in
// the config chain, which validates env at import time.

type EarnVaultWarning = NonNullable<UserEarnTransaction['vault']>['warnings'][number]

function vaultWarningMessage(
  warning: EarnVaultWarning,
  vaultKind: UserEarnTransaction['vaultKind']
): string | undefined {
  if (warning.type === 'custom' && warning.content) return warning.content

  // The message helpers only read `type`, and V2's reads `metadata.history` to tell deprecated vaults apart.
  if (vaultKind === 'vaultV2') {
    const v2Warning = { type: warning.type, metadata: { history: warning.listingHistory } }
    // oxlint-disable-next-line typescript/consistent-type-assertions
    return getVaultV2WarningMessage(v2Warning as never) ?? undefined
  }
  // oxlint-disable-next-line typescript/consistent-type-assertions
  return getVaultWarningMessage(warning as never) ?? undefined
}

/**
 * Maps a vault's warnings onto the shared `Warning` shape for the earn-activity table.
 *
 * Two conversions are load-bearing and must both survive: REST levels are lowercase and
 * score as green through the enum helpers unless normalised, and `bad_debt_unrealized`
 * shares its string with the market type whose scroll target no vault surface has.
 */
export function mapEarnActivityWarnings(
  warnings: readonly EarnVaultWarning[],
  vaultKind: UserEarnTransaction['vaultKind']
): Warning[] {
  return warnings
    .map(warning => {
      const message = vaultWarningMessage(warning, vaultKind)
      if (!isDefined(message)) return undefined
      return {
        level: toWarningLevel(warning.level),
        message,
        warningType: getVaultWarningScrollType(warning.type)
      }
    })
    .filter(isDefined)
}
